import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Pencil, Loader2, Archive, RotateCcw, Building2 } from "lucide-react";
import api from "../lib/api";
import { useStable } from "../context/StableContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { RelationshipView } from "../components/RelationshipView";
import { PartyDeleteZone } from "../components/PartyDeleteZone";
import { PartyConvertZone } from "../components/PartyConvertZone";

export default function OrganizationDetail() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { current, role, assist } = useStable();
  const canEdit = assist ? false : ["manager", "admin"].includes(role);
  const [org, setOrg] = useState(null);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = () => {
    if (!current?.id || !orgId) return;
    setOrg(null); setError(false);
    api.get(`/stables/${current.id}/organizations/${orgId}`)
      .then((r) => setOrg(r.data))
      .catch(() => setError(true));
  };
  useEffect(load, [current?.id, orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="org-detail-error">
        <BackBtn onClick={() => navigate("/organizzazioni")} />
        <div className="text-center py-16 text-[#5F6B64]">Organizzazione non trovata o non collegata a questa scuderia.</div>
      </div>
    );
  }
  if (!org) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="org-detail-loading">
        <BackBtn onClick={() => navigate("/organizzazioni")} />
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#1B3B2B]" /></div>
      </div>
    );
  }

  const toggleArchive = async () => {
    if (!org.archived && !window.confirm("Archiviando questa organizzazione non verranno eliminati dati o storico. Non verrà normalmente proposta per nuove operazioni in questa scuderia.")) return;
    try {
      await api.post(`/stables/${current.id}/organizations/${org.id}/${org.archived ? "restore" : "archive"}`);
      toast.success(org.archived ? "Organizzazione ripristinata" : "Organizzazione archiviata");
      setOrg((o) => ({ ...o, archived: !o.archived }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="org-detail-page">
      <BackBtn onClick={() => navigate("/organizzazioni")} />

      <div className="bg-white rounded-xl border border-[#E5DEC9] p-6">
        <div className="flex items-center gap-4">
          <span className="h-14 w-14 shrink-0 rounded-lg bg-[#8C532B]/10 text-[#8C532B] flex items-center justify-center">
            <Building2 className="h-7 w-7" />
          </span>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-[#1B3B2B] truncate" data-testid="org-detail-name">
              {org.legal_name || "Senza nome"}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {org.organization_type && <Badge variant="secondary" className="bg-[#F0EAE1] text-[#1B3B2B] hover:bg-[#F0EAE1] font-normal">{org.organization_type}</Badge>}
              {org.archived && (
                <Badge variant="outline" className="border-[#9AA39C] text-[#5F6B64] font-normal" data-testid="org-detail-archived-badge">Archiviata</Badge>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <Field label="Ragione sociale" value={org.legal_name} />
          <Field label="Tipo" value={org.organization_type} />
          <Field label="Partita IVA" value={org.partita_iva} />
          <Field label="Codice fiscale" value={org.codice_fiscale} />
          <Field label="Email" value={org.email} />
          <Field label="Telefono" value={org.phone} />
          <Field label="PEC" value={org.pec} />
        </div>

        {canEdit && (
          <div className="mt-6 flex flex-wrap gap-2">
            <Button data-testid="org-detail-edit-btn" onClick={() => setEditing(true)} className="bg-[#1B3B2B] hover:bg-[#142d21]">
              <Pencil className="h-4 w-4 mr-2" /> Modifica
            </Button>
            <Button data-testid="org-detail-archive-btn" variant="outline" onClick={toggleArchive}>
              {org.archived ? <><RotateCcw className="h-4 w-4 mr-2" /> Ripristina</> : <><Archive className="h-4 w-4 mr-2" /> Archivia</>}
            </Button>
          </div>
        )}
      </div>

      <RelationshipView stableId={current.id} partyType="organization" partyId={org.id} />

      {canEdit && (
        <PartyConvertZone stableId={current.id} partyType="organization" partyId={org.id} name={org.legal_name || "questa organizzazione"}
          onConverted={(t, id) => navigate(t === "person" ? `/persone/${id}` : "/organizzazioni")} />
      )}

      {canEdit && (
        <PartyDeleteZone stableId={current.id} partyType="organization" partyId={org.id} name={org.legal_name || "questa organizzazione"}
          onArchive={org.archived ? null : async () => {
            try {
              await api.post(`/stables/${current.id}/organizations/${org.id}/archive`);
              toast.success("Organizzazione archiviata");
              setOrg((o) => ({ ...o, archived: true }));
            } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
          }}
          onDeleted={() => navigate("/organizzazioni")} />
      )}

      {editing && (
        <EditOrgModal org={org} stableId={current.id} onClose={() => setEditing(false)}
          onSaved={(updated) => { setOrg((o) => ({ ...o, ...updated })); setEditing(false); }} />
      )}
    </div>
  );
}

function BackBtn({ onClick }) {
  return (
    <button onClick={onClick} data-testid="org-detail-back"
      className="flex items-center gap-1 text-sm text-[#5F6B64] mb-4 hover:text-[#1B3B2B]">
      <ArrowLeft className="h-4 w-4" /> Organizzazioni
    </button>
  );
}

function Field({ label, value }) {
  return (
    <div className="space-y-0.5">
      <p className="font-mono-label text-[10px] uppercase tracking-wide text-[#9AA39C]">{label}</p>
      <p className="text-sm text-[#1B3B2B] break-words">{value || <span className="text-[#9AA39C]">—</span>}</p>
    </div>
  );
}

function EditOrgModal({ org, stableId, onClose, onSaved }) {
  const [form, setForm] = useState({
    legal_name: org.legal_name || "",
    organization_type: org.organization_type || "",
    partita_iva: org.partita_iva || "",
    codice_fiscale: org.codice_fiscale || "",
    email: org.email || "",
    phone: org.phone || "",
    pec: org.pec || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.legal_name.trim()) { toast.error("La ragione sociale è obbligatoria"); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/stables/${stableId}/organizations/${org.id}`, form);
      if (data?.piva_duplicate_warning) toast.warning("Un'altra organizzazione ha la stessa Partita IVA.");
      toast.success("Organizzazione aggiornata");
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const fields = [
    ["legal_name", "Ragione sociale"],
    ["organization_type", "Tipo"],
    ["partita_iva", "Partita IVA"],
    ["codice_fiscale", "Codice fiscale"],
    ["email", "Email"],
    ["phone", "Telefono"],
    ["pec", "PEC"],
  ];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="org-edit-modal" className="max-h-[85vh] overflow-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle>Modifica organizzazione</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {fields.map(([k, lbl]) => (
            <div key={k} className="space-y-1">
              <Label htmlFor={`oe-${k}`} className="text-xs text-[#5F6B64]">{lbl}</Label>
              <Input id={`oe-${k}`} data-testid={`org-field-${k}`} value={form[k]}
                onChange={(e) => set(k, e.target.value)} autoFocus={false} autoComplete="off" />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="org-cancel-btn">Annulla</Button>
          <Button className="bg-[#1B3B2B] hover:bg-[#142d21]" onClick={save} disabled={saving} data-testid="org-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
